import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import API, { formatApiErrorDetail } from "../lib/api";
import AdminLayout from "./AdminLayout";
import { AuthedImage, AuthedVideo } from "../components/AuthedMedia";
import { useAppT } from "../components/home/i18n";
import { Flag, Trash2, Pin, ShieldCheck, CheckCircle2, Eye } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const mediaUrl = (id) => `${BACKEND}/api/community/media/${id}/file`;

function TargetPreview({ report, a }) {
  const t = report.target;
  if (!t) return <div className="text-xs text-[#8B3A3A]">{a.adminComm.contentRemoved}</div>;
  if (report.target_type === "post") {
    return (
      <div>
        <div className="text-[11px] font-semibold text-[#8a6f38]">{a.catMap[t.category] || t.category}</div>
        <div className="font-serif text-base text-[#173A2C]">{t.title}</div>
        <div className="text-sm text-[#2C2A29]">{t.body}</div>
        {t.media?.length > 0 && <div className="mt-1 flex gap-2">{t.media.map((m) => m.kind === "post_video"
          ? <video key={m.id} src={mediaUrl(m.id)} className="h-16 w-24 rounded-sm bg-black object-cover" muted />
          : <img key={m.id} src={mediaUrl(m.id)} alt="" className="h-16 w-16 rounded-sm object-cover" />)}</div>}
      </div>
    );
  }
  return <div className="text-sm text-[#2C2A29]">💬 {t.body}</div>;
}

function Reports({ a }) {
  const [reports, setReports] = useState([]);
  const load = async () => { const { data } = await API.get("/admin/community/reports", { params: { status: "open" } }); setReports(data.reports); };
  useEffect(() => { load(); }, []);

  const remove = async (r) => {
    const url = r.target_type === "post" ? `/admin/community/posts/${r.target_id}/remove` : `/admin/community/comments/${r.target_id}/remove`;
    try { await API.post(url, { reason: "Removed after report review" }); toast.success(a.adminComm.contentRemovedToast); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const dismiss = async (r) => { try { await API.post(`/admin/community/reports/${r.id}/resolve`); toast.success(a.adminComm.reportDismissed); load(); } catch { toast.error(a.adminComm.failed); } };

  return (
    <div className="mt-6 space-y-3" data-testid="reports-list">
      {reports.length === 0 && <div className="text-sm text-[#5C5855]">{a.adminComm.noReports}</div>}
      {reports.map((r) => (
        <div key={r.id} data-testid={`report-${r.id}`} className="rounded-md border border-[#E8E4DB] bg-white p-4">
          <div className="flex items-center gap-2 text-xs text-[#8B3A3A]"><Flag className="h-3.5 w-3.5" /> {a.adminComm.reportedPrefix} {r.target_type} · {r.reason || a.adminComm.noReasonGiven}</div>
          <div className="mt-2 rounded-sm bg-[#FDFBF7] p-3"><TargetPreview report={r} a={a} /></div>
          <div className="mt-3 flex flex-wrap gap-2">
            <button data-testid={`remove-reported-${r.id}`} onClick={() => remove(r)} className="inline-flex items-center gap-1 rounded-full bg-[#8B3A3A] px-4 py-1.5 text-xs font-semibold text-white"><Trash2 className="h-3.5 w-3.5" /> {a.adminComm.removeContent}</button>
            <button data-testid={`dismiss-report-${r.id}`} onClick={() => dismiss(r)} className="rounded-full border border-[#E8E4DB] px-4 py-1.5 text-xs">{a.adminComm.dismissReport}</button>
          </div>
        </div>
      ))}
    </div>
  );
}

function AllPosts({ a }) {
  const [posts, setPosts] = useState([]);
  const load = async () => { const { data } = await API.get("/admin/community/posts"); setPosts(data.posts); };
  useEffect(() => { load(); }, []);

  const pin = async (p) => { try { await API.post(`/admin/community/posts/${p.id}/pin`, { pinned: !p.pinned, verified_resource: !p.pinned }); load(); } catch { toast.error(a.adminComm.failed); } };
  const remove = async (p) => { try { await API.post(`/admin/community/posts/${p.id}/remove`, { reason: "Removed by moderator" }); load(); } catch { toast.error(a.adminComm.failed); } };

  return (
    <div className="mt-6 space-y-3" data-testid="admin-posts-list">
      {posts.map((p) => (
        <div key={p.id} className="flex flex-wrap items-start justify-between gap-2 rounded-md border border-[#E8E4DB] bg-white p-4">
          <div>
            <div className="flex items-center gap-2 text-[11px]"><span className="font-semibold text-[#8a6f38]">{a.catMap[p.category] || p.category}</span>
              <span className={`rounded-full px-2 py-0.5 font-semibold ${p.status === "visible" ? "bg-[#2E6B4B]/12 text-[#2E6B4B]" : "bg-[#8B3A3A]/10 text-[#8B3A3A]"}`}>{p.status === "visible" ? a.adminComm.visible : p.status}</span>
              {p.report_count > 0 && <span className="text-[#8B3A3A]">{p.report_count} {a.adminComm.reportsSuffix}</span>}
            </div>
            <div className="mt-1 font-serif text-base text-[#173A2C]">{p.title} {p.pinned && <ShieldCheck className="ml-1 inline h-4 w-4 text-[#2E6B4B]" />}</div>
            <div className="text-xs text-[#5C5855]">{a.adminComm.byWord} {p.author?.role === "candidate" ? p.author.alias : p.author?.name} · {a.respMap[p.author?.role] || p.author?.role}</div>
          </div>
          <div className="flex flex-col items-end gap-2">
            <button data-testid={`pin-post-${p.id}`} onClick={() => pin(p)} className={`inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-semibold ${p.pinned ? "bg-[#C4A46B]/20 text-[#8a6f38]" : "border border-[#C4A46B] text-[#8a6f38]"}`}><Pin className="h-3.5 w-3.5" /> {p.pinned ? a.adminComm.unpin : a.adminComm.pinVerified}</button>
            {p.status === "visible" && <button data-testid={`remove-post-${p.id}`} onClick={() => remove(p)} className="inline-flex items-center gap-1 rounded-full border border-[#8B3A3A] px-3 py-1.5 text-xs font-semibold text-[#8B3A3A]"><Trash2 className="h-3.5 w-3.5" /> {a.common.remove}</button>}
          </div>
        </div>
      ))}
    </div>
  );
}

function MediaReview({ a }) {
  const [status, setStatus] = useState("pending");
  const [media, setMedia] = useState([]);
  const [rejectFor, setRejectFor] = useState(null);
  const [note, setNote] = useState("");
  const load = async () => { const { data } = await API.get("/admin/community/media", { params: { status } }); setMedia(data.media); };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const review = async (id, decision, n = "") => {
    try { await API.post(`/admin/community/media/${id}/review`, { decision, note: n }); toast.success(decision === "approved" ? a.adminComm.mediaPublished : a.adminComm.mediaRejected); setRejectFor(null); setNote(""); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="mt-6">
      <div className="mb-3 flex items-center gap-2 rounded-sm bg-[#C4A46B]/10 p-3 text-xs text-[#8a6f38]">
        <Eye className="h-4 w-4 shrink-0" /> {a.adminComm.mediaHint}
      </div>
      <div className="mb-4 flex gap-1 rounded-full border border-[#E8E4DB] bg-white p-1 w-max">
        {["pending", "approved", "rejected"].map((s) => (
          <button key={s} data-testid={`cmedia-filter-${s}`} onClick={() => setStatus(s)} className={`rounded-full px-3 py-1 text-sm ${status === s ? "bg-[#1F4A38] text-white" : "text-[#5C5855]"}`}>{a.adminComm.filters[s]}</button>
        ))}
      </div>
      <div className="grid gap-4 md:grid-cols-2" data-testid="cmedia-list">
        {media.length === 0 && <div className="text-sm text-[#5C5855]">{a.adminComm.noMedia}</div>}
        {media.map((m) => (
          <div key={m.id} data-testid={`cmedia-${m.id}`} className="rounded-md border border-[#E8E4DB] bg-white p-4">
            <div className="text-xs text-[#5C5855]">{a.adminComm.postPrefix} <b className="text-[#2C2A29]">{m.post_title || a.adminComm.untitled}</b> · {m.post_status}</div>
            <div className="mt-2">
              {m.kind === "post_video"
                ? <AuthedVideo path={`/admin/community/media/${m.id}/file`} testid={`cmedia-view-${m.id}`} className="h-52 w-full rounded-sm bg-black object-contain" />
                : <AuthedImage path={`/admin/community/media/${m.id}/file`} className="h-52 w-full rounded-sm object-contain bg-[#F3F0E9]" />}
            </div>
            <div className="mt-1 text-[11px] text-[#8a8580]">{a.adminComm.statusWord} {m.review_status}{m.review_note ? ` · ${m.review_note}` : ""}</div>
            {status === "pending" && (
              <div className="mt-3 flex gap-2">
                <button data-testid={`cmedia-approve-${m.id}`} onClick={() => review(m.id, "approved")} className="inline-flex items-center gap-1 rounded-full bg-[#2E6B4B] px-4 py-1.5 text-xs font-semibold text-white"><CheckCircle2 className="h-3.5 w-3.5" /> {a.adminComm.approve}</button>
                <button data-testid={`cmedia-reject-${m.id}`} onClick={() => setRejectFor(m.id)} className="rounded-full border border-[#8B3A3A] px-4 py-1.5 text-xs font-semibold text-[#8B3A3A]">{a.adminComm.reject}</button>
              </div>
            )}
            {rejectFor === m.id && (
              <div className="mt-3 rounded-sm border border-[#E8E4DB] p-3">
                <textarea data-testid={`cmedia-note-${m.id}`} rows="2" value={note} onChange={(e) => setNote(e.target.value)} placeholder={a.adminComm.rejectPlaceholder} className="w-full rounded-sm border border-[#E8E4DB] px-3 py-2 text-sm outline-none focus:border-[#1F4A38]" />
                <div className="mt-2 flex gap-2">
                  <button data-testid={`cmedia-reject-submit-${m.id}`} onClick={() => note.trim() ? review(m.id, "rejected", note) : toast.error(a.adminComm.noteRequired)} className="rounded-sm bg-[#8B3A3A] px-3 py-1.5 text-xs font-semibold text-white">{a.adminComm.confirmReject}</button>
                  <button onClick={() => { setRejectFor(null); setNote(""); }} className="rounded-sm border border-[#E8E4DB] px-3 py-1.5 text-xs">{a.common.cancel}</button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AdminCommunity() {
  const { a } = useAppT();
  const [tab, setTab] = useState("reports");
  return (
    <AdminLayout>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="font-serif text-3xl text-[#173A2C]">{a.adminComm.title}</h1>
        <div className="flex gap-1 rounded-full border border-[#E8E4DB] bg-white p-1">
          {[["reports", a.adminComm.tabs.reports], ["media", a.adminComm.tabs.media], ["posts", a.adminComm.tabs.posts]].map(([k, l]) => (
            <button key={k} data-testid={`modtab-${k}`} onClick={() => setTab(k)} className={`rounded-full px-3 py-1 text-sm ${tab === k ? "bg-[#1F4A38] text-white" : "text-[#5C5855]"}`}>{l}</button>
          ))}
        </div>
      </div>
      {tab === "reports" ? <Reports a={a} /> : tab === "media" ? <MediaReview a={a} /> : <AllPosts a={a} />}
    </AdminLayout>
  );
}
